import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { skillLabel } from "@/lib/constants";
import { VOLUNTEER } from "@/constants/testIds";
import { OrgRatingChip } from "@/components/OrgRatingChip";
import { imgSrc } from "@/lib/img";
import { formatEventDate } from "@/lib/dates";
import { MapPin, Clock, ChevronRight, Sparkles, ShieldCheck } from "lucide-react";

// Single result card on /opportunities (extracted from Opportunities.jsx in
// the Phase 15 component split — behaviour unchanged).
export function OpportunityCard({ o, idx, rating }) {
  return (
    <Link
      to={`/opportunities/${o.id}`}
      data-testid={VOLUNTEER.oppCard(o.id)}
      className="block bg-white border border-neutral-200 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all animate-fade-up"
      style={{ animationDelay: `${Math.min(idx, 6) * 60}ms` }}
    >
      <div className="flex items-stretch">
        {o.cover_image && (
          <div className="hidden sm:block w-44 shrink-0">
            <img src={imgSrc(o.cover_image)} alt={o.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex items-start justify-between gap-4 flex-wrap p-6 flex-1">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-terracotta/80 font-bold inline-flex items-center gap-2">
            {o.org_name}
            <OrgRatingChip rating={rating} testId={`org-rating-chip-${o.id}`} />
          </div>
          <div className="font-display text-xl text-forest mt-1">{o.title}</div>
          <div className="text-sm text-neutral-600 mt-2 max-w-2xl line-clamp-2">{o.description}</div>
          <div className="flex flex-wrap gap-3 mt-4 text-sm text-neutral-600">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-forest/60" /> {o.remote ? "Remote" : o.location}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-forest/60" /> {o.hours_required}h</span>
            {o.event_date && (
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-forest/60" /> {formatEventDate(o.event_date)}</span>
            )}
            {o.is_help_request ? (
              <Badge data-testid="help-request-badge" className="rounded-full text-[11px] bg-sky-600 text-white border-none">Help request</Badge>
            ) : o.community_led && (
              <Badge data-testid={`community-led-badge-${o.id}`} className="rounded-full text-[11px] bg-terracotta text-white border-none">Community-led</Badge>
            )}
            {o.remote && (
              <Badge className="rounded-full text-[11px] bg-forest/10 text-forest border-none">Remote</Badge>
            )}
            {o.collective_id && (
              <Badge data-testid="opp-collective-chip" className="rounded-full text-[11px] bg-forest/10 text-forest border border-forest/20">
                🏘️ Collective
              </Badge>
            )}
            {o.referred && (
              <Badge data-testid="opp-referred-chip" className="rounded-full text-[11px] bg-forest text-white border-none">
                Referred by a local professional
              </Badge>
            )}
            {o.commitment_type && (
              <Badge
                data-testid={`opp-commitment-chip-${o.commitment_type}`}
                className={`rounded-full text-[11px] border-none ${
                  o.commitment_type === "micro"
                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                    : "bg-forest/10 text-forest"
                }`}
              >
                {o.commitment_type === "micro" && "⚡ "}
                {o.commitment_type === "micro" ? "Under an hour" : o.commitment_type === "one_off" ? "One-off" : o.commitment_type === "recurring" ? "Recurring" : "Ongoing"}
              </Badge>
            )}
            {typeof o.max_volunteers === "number" && o.max_volunteers > 0 && (
              o.is_full ? (
                <Badge data-testid="opp-full-badge" className="rounded-full text-[11px] bg-red-100 text-red-700 border-none">Full · {o.spots_taken}/{o.max_volunteers}</Badge>
              ) : (
                <Badge data-testid="opp-spots-badge" className="rounded-full text-[11px] bg-neutral-100 text-neutral-700 border-none">{o.spots_taken}/{o.max_volunteers} spots</Badge>
              )
            )}
            {/* B4: requirement badges — self-attested, host verifies documents */}
            {o.dbs_required && (
              <Badge data-testid={`opp-dbs-badge-${o.id}`} className="rounded-full text-[11px] bg-indigo-100 text-indigo-800 border-none inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> DBS required
              </Badge>
            )}
            {o.required_age && (
              <Badge data-testid={`opp-age-badge-${o.id}`} className="rounded-full text-[11px] bg-neutral-100 text-neutral-700 border-none">{o.required_age}+</Badge>
            )}
            {o.distance_km != null && (
              <span className="inline-flex items-center gap-1.5 text-forest font-medium">{o.distance_km} km away</span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {o.values?.map(v => <Badge key={v} variant="outline" className="rounded-full text-xs capitalize border-forest/20 text-forest">{v}</Badge>)}
            {o.skills_needed?.map(s => <Badge key={s} className="rounded-full text-xs capitalize bg-terracotta/10 text-terracotta border-none">{skillLabel(s)}</Badge>)}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-neutral-400 mt-1" />
        </div>
      </div>
    </Link>
  );
}
